import { PocButton, PocDatepicker, PocIcon, PocSwitch, PocTypography } from 'commons/components/components-wrapper';
import {
    DatepickerType,
    DatepickerValueType,
    InputValidity,
    TypographyVariant,
} from '@one/web-components/dist/types/components';
import { useTranslation, TFunction } from 'core/i18n/i18n';
import { CustomValidationCode } from 'core/utils/backend-service';
import { RefObject, memo, useMemo, useRef, useState } from 'react';
import { AssistiveText, AssistiveTextLayout, ExtendedValidationMessages } from '../form-elements';
import { DatePickerLayout } from './date-picker.style';

interface DatePickerProps {
    name: string;
    value: DatepickerValueType;
    secondaryValue?: DatepickerValueType;
    placeholder?: string;
    datePickerType?: DatepickerType;
    isFilter?: boolean;
    label?: string;
    labelVariant?: TypographyVariant;
    inline?: string;
    className?: string;
    spaceForError?: boolean;
    floatingAssistiveText?: boolean;
    format?: string;
    minDate?: Date;
    maxDate?: Date;
    validity?: InputValidity;
    disabled?: boolean;
    autoClose?: boolean;
    applyDisabled?: boolean;
    quick?: boolean;
    isRangeAvailable?: boolean;
    footerInfo?: React.ReactElement;
    datepickerRef?: RefObject<HTMLOwcDatepickerElement>;
    nonEditableInput?: boolean;
    validationMessages?: ExtendedValidationMessages;
    customValidationCode?: CustomValidationCode;
    required?: boolean;
    children?: React.ReactNode;
    onChangeDatePickerType?: (type: DatepickerType) => void;
    onChange: (field: string, value: DatepickerValueType) => void;
    onClear?: VoidFunction;
    onValidity?: (field: string, validity: InputValidity) => void;
    onApply?: VoidFunction;
}

export const MIN_YEAR = 1700;

const getCalendarFooter = (
    isRangeAvailable: boolean,
    isDateRange: boolean,
    autoClose: boolean,
    checkDisabled: boolean,
    handleOnApply: VoidFunction,
    t: TFunction,
    onChangeDatePickerType?: (type: DatepickerType) => void
) => (
    <div>
        {isRangeAvailable && onChangeDatePickerType ? (
            <PocSwitch
                checked={isDateRange}
                labelPlacement="left"
                onValueChange={() => onChangeDatePickerType(isDateRange ? 'single' : 'range')}
            >
                {t('commons.components.datepicker.footer.range-selector')}
            </PocSwitch>
        ) : undefined}
        {!autoClose && (
            <PocButton variant="primary" onClick={handleOnApply} disabled={checkDisabled}>
                {t('commons.components.datepicker.footer.apply')}
            </PocButton>
        )}
    </div>
);

const getFooterInfo = (footerInfo?: React.ReactElement) => (
    <>
        {footerInfo ? (
            <div className={'footer-info'} style={{ maxWidth: '312px', marginTop: '0.5rem' }}>
                <PocIcon name="circle_info"></PocIcon>
                {footerInfo}
            </div>
        ) : undefined}
    </>
);

const getLabel = (
    isFilter: boolean,
    t: TFunction,
    inline?: string,
    label?: string,
    labelVariant?: TypographyVariant
) => (
    <>
        {label && !isFilter && (
            <PocTypography element="span" variant={labelVariant} className={`label ${inline ? 'inline' : 'block'}`}>
                {t(label)}
            </PocTypography>
        )}
    </>
);

export const DatePicker: React.FC<DatePickerProps> = memo((props) => {
    const {
        name,
        value,
        secondaryValue,
        placeholder,
        datePickerType = 'single',
        label,
        labelVariant = 'subtitle2',
        inline,
        className = '',
        disabled,
        minDate = new Date(MIN_YEAR),
        format = 'MM/dd/yyyy',
        validity,
        maxDate,
        quick = false,
        isFilter = false,
        autoClose = true,
        applyDisabled = false,
        isRangeAvailable = false,
        footerInfo,
        datepickerRef,
        nonEditableInput = false,
        spaceForError = false,
        floatingAssistiveText = false,
        validationMessages = {},
        customValidationCode,
        required = true,
        children,
        onChangeDatePickerType,
        onChange,
        onClear,
        onApply,
        onValidity,
    } = props;

    const localDatepickerRef = useRef<HTMLOwcDatepickerElement>(null);
    const effectiveDatepickerRef = datepickerRef ?? localDatepickerRef;

    const [isVisible, setIsVisible] = useState<boolean>(false);

    const { t } = useTranslation();

    const isDateRange = useMemo((): boolean => datePickerType === 'range', [datePickerType]);

    const checkDisabled = useMemo(
        (): boolean => applyDisabled || !value || (isDateRange && (value as Date[]).length !== 2),
        [applyDisabled, isDateRange, value]
    );

    const handleOnApply = () => {
        onApply?.();
        if (!autoClose) {
            setCalendarVisible(false);
        }
    };

    const setCalendarVisible = (visible: boolean) => {
        setIsVisible(visible);
        effectiveDatepickerRef.current?.setCalendarVisible(visible);
    };

    const getUTCValue = (detail: DatepickerValueType) => {
        if (!detail) {
            return detail;
        }
        return Array.isArray(detail) ? detail.map((date) => date) : detail;
    };

    return (
        <DatePickerLayout
            className={`${inline ? 'inline' : 'block'} ${className}`}
            style={isFilter ? { padding: 0 } : {}}
        >
            {getLabel(isFilter, t, inline, label, labelVariant)}
            <PocDatepicker
                data-testid={`date-picker-${name}`}
                ref={effectiveDatepickerRef}
                name={name}
                label={isFilter ? label : undefined}
                maxDate={maxDate}
                type={isDateRange ? 'range' : 'single'}
                variant="outlined"
                className={`${spaceForError ? 'space-for-error' : ''} ${floatingAssistiveText ? 'floating-assistive-text' : ''}`}
                format={format}
                onClearClick={onClear}
                value={secondaryValue !== undefined && !isVisible ? secondaryValue : value}
                placeholder={placeholder ?? format.toLowerCase()}
                minDate={minDate}
                quick={quick}
                required={required}
                autoClose={autoClose}
                validity={validity}
                controlledValidity={validity !== undefined || onValidity !== undefined}
                disabled={disabled}
                inputProps={
                    nonEditableInput
                        ? {
                              tabIndex: -1,
                              focusedView: isVisible,
                              onmousedown: (e) => e.preventDefault(),
                              nativeInputProps: {
                                  readOnly: true,
                                  tabIndex: -1,
                                  onmousedown: (e) => e.preventDefault(),
                                  onclick: () => setIsVisible(!isVisible),
                              },
                          }
                        : {}
                }
                popoverProps={{
                    visible: isVisible,
                    onClickOutside: () => setIsVisible(false),
                }}
                onValueChange={(ev) => {
                    onChange(name, getUTCValue(ev.detail));
                }}
                onValidityChange={(ev) => onValidity?.(name, ev.detail)}
                onPanelOpen={() => setIsVisible(true)}
                onPanelClose={() => setIsVisible(false)}
            >
                <div slot="calendar-footer">
                    {getCalendarFooter(
                        isRangeAvailable,
                        isDateRange,
                        autoClose,
                        checkDisabled,
                        handleOnApply,
                        t,
                        onChangeDatePickerType
                    )}
                    {getFooterInfo(footerInfo)}
                </div>
                {children}
                <AssistiveTextLayout slot="assistive-text">
                    <AssistiveText
                        validity={validity}
                        validationMessages={validationMessages}
                        customValidationCode={customValidationCode}
                    />
                </AssistiveTextLayout>
            </PocDatepicker>
        </DatePickerLayout>
    );
});
